import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SprayOperator, SprayMachine, IntensityLabel, ComboRegenStatus } from '../../services/sprayService';
import { suggestIntensityLevel, SuggestionResult } from '../../utils/suggestIntensityLevel';
import CalibrationStatusBadge from './CalibrationStatusBadge';
import OperatorLicencePill from './statusPills/OperatorLicencePill';
import { formatLitres } from '../../utils/planningSummaryFormat';

export interface ComboInput {
  _id?: string;
  operatorId: string;
  machineId: string;
  intensityLevel?: IntensityLabel;     // Phase 6b — per-combo intensity
  intensityUserOverrode?: boolean;     // Phase 6b — per-row sticky override flag (UI-only)
  // PDF auto-gen — populated by SpraySessionForm when initialising from session.combos.
  // Only present on saved combos (those with a server-side `_id`).
  regenStatus?: ComboRegenStatus;
  instructionId?: string | null;
}

export interface ComboListEditorProps {
  value: ComboInput[];
  onChange: (next: ComboInput[]) => void;
  operators: SprayOperator[];
  machines: SprayMachine[];
  disabled?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  /**
   * Selected round metadata (Phase 6b). Optional during the migration window
   * — Task 6b.12 wires this from the form. When present and waterPerHectare
   * is positive, the editor auto-fills `intensityLevel` per row using the
   * suggestion algorithm. Rows with `intensityUserOverrode === true` are
   * never overwritten.
   */
  selectedRound?: { waterPerHectare?: number } | null;
  /**
   * Optional slot for per-row trailing actions. Rendered after the remove
   * button on each combo row. Fires for:
   *   - saved combos (those with a server-side `_id`), AND
   *   - unsaved rows where both operatorId and machineId are filled.
   * Incomplete unsaved rows (missing operator or machine) get an empty slot.
   * Used by SpraySessionForm to surface ComboInstructionCell for saved combos
   * and a Generate shortcut button for valid-but-unsaved rows.
   */
  renderRowActions?: (combo: ComboInput) => React.ReactNode;
}

function findDuplicateOperatorRows(value: ComboInput[]): Set<number> {
  const counts = new Map<string, number>();
  for (const combo of value) {
    const id = combo?.operatorId;
    if (!id) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const duplicateIds = new Set<string>();
  counts.forEach((count, id) => {
    if (count > 1) duplicateIds.add(id);
  });
  const rows = new Set<number>();
  value.forEach((combo, index) => {
    if (combo?.operatorId && duplicateIds.has(combo.operatorId)) {
      rows.add(index);
    }
  });
  return rows;
}

/**
 * Latest non-deleted calibration for a machine, or null. Mirrors the helper
 * in SpraySessionForm but local to the editor since the editor owns
 * per-row suggestions now (Phase 6b).
 */
function getLatestCalibration(machine: SprayMachine | undefined) {
  if (!machine?.calibrations || machine.calibrations.length === 0) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const active = machine.calibrations.filter((c: any) => c.status !== 'deleted');
  if (active.length === 0) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return [...active].sort((a: any, b: any) =>
    new Date(b.date).getTime() - new Date(a.date).getTime(),
  )[0];
}

function suggestionForRow(
  combo: ComboInput,
  machines: SprayMachine[],
  roundWph?: number,
): SuggestionResult {
  const machine = machines.find(m => m._id === combo.machineId);
  const latest = getLatestCalibration(machine);
  return suggestIntensityLevel(roundWph, latest?.intensityLevels);
}

const labelToKey = (label: string) =>
  label === 'medium/heavy' ? 'mediumHeavy' : label;

const ComboListEditor: React.FC<ComboListEditorProps> = ({
  value,
  onChange,
  operators,
  machines,
  disabled = false,
  onValidityChange,
  selectedRound,
  renderRowActions,
}) => {
  const { t } = useTranslation();

  const duplicateRows = useMemo(() => findDuplicateOperatorRows(value), [value]);
  const isValid = duplicateRows.size === 0;

  useEffect(() => {
    if (onValidityChange) onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  // Phase 6b — per-row suggestion auto-fill. Runs whenever value, machines,
  // or round.waterPerHectare changes. Skips rows where the user has
  // overridden (`intensityUserOverrode === true`). Calls onChange only when
  // at least one row's suggestion actually differs from the current value,
  // which is the infinite-loop guard: if every suggestion already matches,
  // `needsUpdate` stays false and we no-op.
  const roundWph = selectedRound?.waterPerHectare;
  useEffect(() => {
    if (!roundWph) return;
    let needsUpdate = false;
    const next = value.map(combo => {
      if (combo.intensityUserOverrode) return combo;
      const s = suggestionForRow(combo, machines, roundWph);
      if (s.label && s.label !== combo.intensityLevel) {
        needsUpdate = true;
        return { ...combo, intensityLevel: s.label };
      }
      return combo;
    });
    if (needsUpdate) onChange(next);
  }, [value, machines, roundWph, onChange]);

  // Phase 6b UX hardening — validate-on-load. When the form first mounts
  // (or `machines` finishes loading), a combo loaded from the server may
  // carry an `intensityLevel` that is no longer on the machine's latest
  // calibration — e.g. the calibration was amended after the session was
  // planned. The per-row dropdown only renders labels from the calibration,
  // so the `<select>`'s `value` would point at a non-existent option. Clear
  // those stale values here and drop the sticky override flag so the
  // auto-fill effect can immediately re-suggest a valid label.
  //
  // Dep list deliberately excludes `value` — this runs once `machines`
  // loads (or changes between loads), not on every user keystroke. That
  // means a stale intensity introduced AFTER mount (e.g. by a sanitizer-
  // bypassing code path or a test using `fireEvent.change`) flows through
  // to `handleSave`, where the existing `isIntensityOnMachineCalibration`
  // backstop strips it from the outbound patch.
  useEffect(() => {
    if (machines.length === 0) return;
    let needsUpdate = false;
    const next = value.map(combo => {
      if (combo.intensityLevel == null) return combo;
      const machine = machines.find(m => m._id === combo.machineId);
      const latest = getLatestCalibration(machine);
      const labels = (latest?.intensityLevels ?? []).map(l => l.label);
      if (!labels.includes(combo.intensityLevel)) {
        needsUpdate = true;
        return { ...combo, intensityLevel: undefined, intensityUserOverrode: false };
      }
      return combo;
    });
    if (needsUpdate) onChange(next);
    // Intentionally only on machines change; see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machines]);

  const updateRow = (index: number, patch: Partial<ComboInput>) => {
    const next = value.map((combo, i) => (i === index ? { ...combo, ...patch } : combo));
    onChange(next);
  };

  /**
   * Phase 6b — machine-change override reset. If the existing
   * `intensityLevel` is still a valid label on the new machine's latest
   * calibration, keep it (and keep the sticky override flag). Otherwise
   * clear both intensity + override and immediately apply the suggestion
   * for the new machine so the user never sees a transient empty state.
   * Computing the suggestion inline (rather than relying on the auto-fill
   * effect) also makes the change a single onChange call, which keeps the
   * controlled-component contract clean for callers.
   */
  const handleMachineChange = (index: number, newMachineId: string) => {
    const combo = value[index];
    const newMachine = machines.find(m => m._id === newMachineId);
    const latest = getLatestCalibration(newMachine);
    const availableLabels = (latest?.intensityLevels || []).map(l => l.label);
    const existingValid =
      combo.intensityLevel != null && availableLabels.includes(combo.intensityLevel);

    if (existingValid) {
      updateRow(index, { machineId: newMachineId });
    } else {
      const suggested = suggestIntensityLevel(roundWph, latest?.intensityLevels);
      updateRow(index, {
        machineId: newMachineId,
        intensityLevel: suggested.label ?? undefined,
        intensityUserOverrode: false,
      });
    }
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...value, { operatorId: '', machineId: '' }]);
  };

  return (
    <div className="combo-list-editor" data-testid="combo-list-editor">
      <div className="pat-field__label mb-2">{t('spray.sessions.combos.sectionTitle')}</div>

      {value.length === 0 && (
        <p className="text-muted small mb-2">{t('spray.sessions.combos.emptyState')}</p>
      )}

      {value.map((combo, index) => {
        const isDuplicate = duplicateRows.has(index);
        const rowKey = combo._id ?? `row-${index}`;
        const suggestion = suggestionForRow(combo, machines, roundWph);
        // Phase 6b — derive the dropdown options from the chosen machine's
        // latest active calibration. This makes invalid picks impossible
        // and removes the silent-drop UX surprise where the save flow's
        // sanitization helper would discard an enum value the calibration
        // didn't define. The sanitization remains as a defensive backstop.
        const rowMachine = machines.find(m => m._id === combo.machineId);
        const rowOperator = operators.find(o => o._id === combo.operatorId);
        const rowLatest = getLatestCalibration(rowMachine);
        const availableLabels: IntensityLabel[] =
          rowLatest?.intensityLevels?.map(l => l.label) ?? [];
        return (
          <div
            key={rowKey}
            className="d-flex align-items-start gap-2 mb-2"
            data-testid={`combo-row-${index}`}
          >
            <div className={`pat-field${isDuplicate ? ' pat-field--error' : ''}`} style={{ flex: 1 }}>
              <label htmlFor={`combo-operator-${index}`} className="visually-hidden">
                {t('spray.sessions.combos.operatorLabel')}
              </label>
              <select
                id={`combo-operator-${index}`}
                className="pat-input"
                aria-label={t('spray.sessions.combos.operatorLabel')}
                aria-invalid={isDuplicate || undefined}
                value={combo.operatorId}
                onChange={e => updateRow(index, { operatorId: e.target.value })}
                disabled={disabled}
              >
                <option value="">{t('spray.sessions.combos.operatorPlaceholder')}</option>
                {operators.map(op => (
                  <option key={op._id} value={op._id}>
                    {op.name}
                  </option>
                ))}
              </select>
              {isDuplicate && (
                <div className="text-danger small mt-1" role="alert">
                  {t('spray.sessions.combos.duplicateOperatorError')}
                </div>
              )}
              {rowOperator && (
                <div className="mt-1 text-muted" style={{ fontSize: '0.7rem' }} data-testid={`combo-operator-meta-${index}`}>
                  <div>
                    {t('spray.operators.licenseNo')}:{' '}
                    {rowOperator.licenseNo || '—'}
                  </div>
                  <div className="mt-1">
                    <OperatorLicencePill expiry={rowOperator.licenseExpiry} />
                  </div>
                </div>
              )}
            </div>

            <div className="pat-field" style={{ flex: 1 }}>
              <label htmlFor={`combo-machine-${index}`} className="visually-hidden">
                {t('spray.sessions.combos.machineLabel')}
              </label>
              <select
                id={`combo-machine-${index}`}
                className="pat-input"
                aria-label={t('spray.sessions.combos.machineLabel')}
                value={combo.machineId}
                onChange={e => handleMachineChange(index, e.target.value)}
                disabled={disabled}
              >
                <option value="">{t('spray.sessions.combos.machinePlaceholder')}</option>
                {machines.map(m => (
                  <option key={m._id} value={m._id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {rowMachine && (
                <div className="mt-1 text-muted" style={{ fontSize: '0.7rem' }} data-testid={`combo-machine-meta-${index}`}>
                  <div>
                    {t('spray.sessions.machineTankVolume')}{' '}
                    {rowMachine.tankVolume ? formatLitres(rowMachine.tankVolume) : '—'}
                  </div>
                  <div className="mt-1">
                    <CalibrationStatusBadge machine={rowMachine} />
                  </div>
                </div>
              )}
            </div>

            <div className="pat-field" style={{ flex: 1 }}>
              <label htmlFor={`combo-intensity-${index}`} className="visually-hidden">
                {t('spray.sessions.combos.intensity.label')}
              </label>
              <select
                id={`combo-intensity-${index}`}
                className="pat-input"
                aria-label={t('spray.sessions.combos.intensity.label')}
                value={combo.intensityLevel ?? ''}
                onChange={e =>
                  updateRow(index, {
                    intensityLevel: (e.target.value || undefined) as IntensityLabel | undefined,
                    intensityUserOverrode: true,
                  })
                }
                disabled={disabled}
              >
                <option value="">{t('spray.sessions.combos.intensity.placeholder')}</option>
                {availableLabels.map(label => (
                  <option key={label} value={label}>
                    {t(`spray.sessions.combos.intensity.options.${labelToKey(label)}`)}
                  </option>
                ))}
              </select>

              {/* Phase 6b — per-row helper text. Mirrors the four
                  SuggestionResult.rationale branches, plus a sticky-override
                  variant when the user has manually picked a value. */}
              {(() => {
                if (combo.intensityUserOverrode && suggestion.label) {
                  return (
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                      {t('spray.sessions.combos.intensity.suggestion.userOverrode', {
                        label: t(
                          `spray.sessions.combos.intensity.options.${labelToKey(suggestion.label)}`,
                        ),
                        calibrated: suggestion.matchedLitresPerHectare,
                      })}
                    </div>
                  );
                }
                if (suggestion.rationale === 'closest_match' && suggestion.label) {
                  return (
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                      {t('spray.sessions.combos.intensity.suggestion.closestMatch', {
                        label: t(
                          `spray.sessions.combos.intensity.options.${labelToKey(suggestion.label)}`,
                        ),
                        planned: roundWph,
                        calibrated: suggestion.matchedLitresPerHectare,
                      })}
                    </div>
                  );
                }
                const noKey =
                  suggestion.rationale === 'no_input'
                    ? 'noInput'
                    : suggestion.rationale === 'no_levels'
                      ? 'noLevels'
                      : 'noLitresPerHectare';
                return (
                  <div className="text-muted small mt-1">
                    {t(`spray.sessions.combos.intensity.suggestion.${noKey}`)}
                  </div>
                );
              })()}
            </div>

            <button
              type="button"
              className="pat-btn pat-btn--danger"
              onClick={() => removeRow(index)}
              disabled={disabled}
              aria-label={t('spray.sessions.combos.removeCombo')}
            >
              &times;
            </button>

            {renderRowActions && (combo._id || (combo.operatorId && combo.machineId)) && (
              <div className="d-flex align-items-center">
                {renderRowActions(combo)}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="pat-btn pat-btn--outline"
        onClick={addRow}
        disabled={disabled}
      >
        + {t('spray.sessions.combos.addCombo')}
      </button>

      <div className="text-muted small mt-2">
        {t('spray.sessions.combos.handoverHint')}
      </div>
    </div>
  );
};

export default ComboListEditor;
